import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDeliveryNoteAdminWithRelatedData } from './fetchDeliveryNoteAdminWithRelatedData';
import { type DeliveryNoteAdminWithRelatedData } from './types';

/**
 * Get the query options for fetching a delivery notes with the given ID.
 * @param deliveryNoteId - The ID of the delivery note to fetch.
 * @param options - Optional configuration for the query.
 * @returns The query options.
 */
export const getDeliveryNoteAdminQueryOptions = ({
  deliveryNoteId,
  options = {},
}: {
  deliveryNoteId: UUID;
  options?: Omit<
    UseQueryOptions<DeliveryNoteAdminWithRelatedData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  return {
    enabled: Boolean(deliveryNoteId),
    queryFn: async ({ signal }: { signal: AbortSignal }) =>
      fetchDeliveryNoteAdminWithRelatedData(deliveryNoteId, { signal }),
    queryKey: queryKeysDeliveryNote.get(deliveryNoteId),
    staleTime: ms('30s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for a delivery note with a given ID.
 * @param {UUID} deliveryNoteId - The ID of the delivery note to fetch.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult<DeliveryNoteListItem|undefined>} The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/get_delivery_note_by_id_vadmin_asset_delivery_note__id__get
 */
export const useQueryDeliveryNoteAdmin = <
  TData = DeliveryNoteAdminWithRelatedData,
>(
  deliveryNoteId: UUID,
  options?: Omit<UseQueryOptions<TData, Error, TData>, 'queryKey' | 'queryFn'>,
): UseQueryResult<TData> => {
  // @ts-expect-error - wth
  return useQuery({
    ...getDeliveryNoteAdminQueryOptions({
      deliveryNoteId,
      // @ts-expect-error - wth
      options,
    }),
  });
};

/**
 * React Query based custom hook for getting the data for a delivery note with a given ID.
 * @param deliveryNoteId - The ID of the delivery note to fetch.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/get_delivery_note_by_id_vadmin_asset_delivery_note__id__get
 */
export const useSuspenseQueryDeliveryNoteAdmin = <
  TData = DeliveryNoteAdminWithRelatedData,
>(
  deliveryNoteId: UUID,
  options?: Omit<UseQueryOptions<TData, Error, TData>, 'queryKey' | 'queryFn'>,
): UseSuspenseQueryResult<TData> => {
  // @ts-expect-error - wth
  return useSuspenseQuery({
    ...getDeliveryNoteAdminQueryOptions({
      deliveryNoteId,
      // @ts-expect-error - wth
      options,
    }),
  });
};
